import './KeyInsights.css';
import { useState } from 'react';
import { HiOutlineChevronDown, HiOutlineOfficeBuilding } from 'react-icons/hi';
import Spinner from '../Spinner/Spinner';
import Tooltip from '../Tooltip/Tooltip';

interface KeyInsightsProps {
  totalServiceableSites?: number;
  totalAcquiredSites?: number;
  retailersWithNoPenetration?: number;
  totalOpportunities?: number;
  highCrimeSites?: number;
  highCrimeSiteRetailers?: number;
  highGkapSites?: number;
  highGkapRetailers?: number;
  newInstallCount?: number;
  existingCount?: number;
  isLoading?: boolean;
}

function KeyInsights({
  totalServiceableSites = 2330,
  totalAcquiredSites = 1190,
  retailersWithNoPenetration = 0,
  totalOpportunities = 76200,
  highCrimeSites = 0,
  highGkapSites = 210,
  newInstallCount = 40900,
  existingCount = 35400,
  isLoading = false,
}: KeyInsightsProps) {
  const [insightsExpanded, setInsightsExpanded] = useState(false);

  const newInstallPercentage = (newInstallCount / totalOpportunities) * 100;
  const existingPercentage = (existingCount / totalOpportunities) * 100;

  return (
    <div className="key-insights-section hidden">
      <div className="key-insights-header" onClick={() => setInsightsExpanded(!insightsExpanded)}>
        <span className="insights-label">Key Insights</span>
        <span className="insights-count">6</span>
        <button
          className={`insights-toggle ${insightsExpanded ? 'expanded' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setInsightsExpanded(!insightsExpanded);
          }}
        >
          <HiOutlineChevronDown />
        </button>
      </div>

      {insightsExpanded && (
        <div className="key-insights-content">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <Spinner size="small" />
            </div>
          ) : (
            <>
              <Tooltip content="All retail store locations eligible for Gatekeeper solutions" side="top">
                <div className="insight-metric">
                  <HiOutlineOfficeBuilding className="metric-icon" />
                  <div className="metric-number">{(totalServiceableSites / 1000).toFixed(2)}K</div>
                  <div className="metric-title">Total Serviceable Sites</div>
                </div>
              </Tooltip>

              <Tooltip content="Retail store locations with at least one Gatekeeper solution installed." side="top">
                <div className="insight-metric">
                  <HiOutlineOfficeBuilding className="metric-icon" />
                  <div className="metric-number">{(totalAcquiredSites / 1000).toFixed(2)}K</div>
                  <div className="metric-title">Total Acquired Sites</div>
                </div>
              </Tooltip>

              <Tooltip content="Retail brands that have zero Gatekeeper installations across all their serviceable store locations." side="top">
                <div className="insight-metric">
                  <HiOutlineOfficeBuilding className="metric-icon" />
                  <div className="metric-number">{retailersWithNoPenetration}</div>
                  <div className="metric-title">Retailers with No Penetration</div>
                </div>
              </Tooltip>

              <Tooltip content="Total Store-level sales opportunities for all solutions identified across serviceable sites" side="top">
                <div className="insight-metric highlight">
                  <HiOutlineOfficeBuilding className="metric-icon" />
                  <div className="metric-number">{(totalOpportunities / 1000).toFixed(1)}K</div>
                  <div className="metric-title">Total Opportunities</div>
                  <div className="opportunity-breakdown">
                    <div className="breakdown-bar">
                      <div className="bar-segment new-install" style={{ width: `${newInstallPercentage}%` }}></div>
                      <div className="bar-segment existing" style={{ width: `${existingPercentage}%` }}></div>
                    </div>
                    <div className="breakdown-legend">
                      <span className="legend-item" >
                        <span className="legend-dot new-install"></span>
                        New Sites <strong>{(newInstallCount / 1000).toFixed(1)}K</strong>
                      </span>
                      <span className="legend-item" >
                        <span className="legend-dot existing"></span>
                        Existing Sites <strong>{(existingCount / 1000).toFixed(1)}K</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </Tooltip>

              <Tooltip content="Serviceable stores in locations with 1000+ retail crimes per year" side="top">
                <div className="insight-metric">
                  <HiOutlineOfficeBuilding className="metric-icon" />
                  <div className="metric-number">{highCrimeSites.toLocaleString()}</div>
                  <div className="metric-title">High Crime Sites</div>
                </div>
              </Tooltip>

              <Tooltip content="Serviceable store locations with 75% or more of Gatekeeper Solutions penetration" side="top">
                <div className="insight-metric">
                  <HiOutlineOfficeBuilding className="metric-icon" />
                  <div className="metric-number">{highGkapSites.toLocaleString()}</div>
                  <div className="metric-title">High GKAP Sites</div>
                </div>
              </Tooltip>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default KeyInsights;
